import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Headset, Send } from 'lucide-react';
import { ApiService } from '../services/apiEndpoints';
import { useAuth } from '../context/AuthContext';
import './Support.css';

// Helper to format ISO string to "hh:mm a"
const formatTime = (isoString) => {
  try {
    const date = new Date(isoString);
    return date.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit', 
      hour12: true 
    });
  } catch (e) {
    return "";
  }
};

const ChatBubble = ({ msg }) => {
  const isUser = msg.sender === 'USER';

  return (
    <div className={`chat-row ${isUser ? 'user-row' : 'admin-row'}`}>
      
      {/* Admin Avatar */}
      {!isUser && (
        <div className="admin-avatar">
          <Headset size={16} color="var(--text-light, #94a3b8)" />
        </div>
      )}

      {/* Bubble */}
      <div className={`chat-bubble ${isUser ? 'user-bubble' : 'admin-bubble'}`}>
        <p className="chat-text">{msg.message}</p>
        <span className="chat-time">{msg.time}</span>
      </div>
      
    </div>
  );
};

const Support = () => {
  const navigate = useNavigate();
  const { studentId } = useAuth();
  
  const [message, setMessage] = useState("");
  const [messages, setMessages] = useState([]);
  const [sending, setSending] = useState(false);
  
  const messagesEndRef = useRef(null);

  // Auto-scroll to bottom
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const loadHistory = async () => {
    if (!studentId) return;
    try {
      const res = await ApiService.getSupportHistory(studentId);
      if (res.data) {
        const uiMessages = [];
        
        // Flatten the history into individual messages
        res.data.forEach(item => {
          // User Message
          if (item.createdAt) {
            uiMessages.push({
              id: `user-${item.createdAt}`, // Unique key fallback
              message: item.message || "",
              time: formatTime(item.createdAt),
              timestamp: new Date(item.createdAt).getTime(),
              sender: 'USER'
            });
          }
          
          // Admin Reply
          if (item.adminReply && item.adminReplyAt) {
            uiMessages.push({
              id: `admin-${item.adminReplyAt}`,
              message: item.adminReply,
              time: formatTime(item.adminReplyAt),
              timestamp: new Date(item.adminReplyAt).getTime(),
              sender: 'ADMIN'
            });
          }
        });

        // Sort chronologically
        uiMessages.sort((a, b) => a.timestamp - b.timestamp);
        setMessages(uiMessages);
      }
    } catch (error) {
      console.error("Failed to load support history", error);
    }
  };

  useEffect(() => {
    loadHistory();
  }, [studentId]);

  const handleSend = async () => {
    if (!message.trim() || sending) return;
    
    setSending(true);
    const msgToSend = message.trim();
    setMessage(""); // Optimistic clear

    try {
      await ApiService.sendSupportMsg({
        studentId: studentId,
        message: msgToSend
      });
      await loadHistory(); // Refresh to get the actual timestamp from server
    } catch (error) {
      console.error("Failed to send message", error);
      setMessage(msgToSend); // Revert on failure
      alert("Failed to send message. Please try again.");
    } finally {
      setSending(false);
    }
  };

  // Handle "Enter" key press
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="support-screen">
      
      {/* Top Bar */}
      <div className="support-top-bar">
        <button onClick={() => navigate(-1)} className="icon-button">
          <ArrowLeft size={24} color="#1e3a8a" />
        </button>
        
        <div className="support-header-info">
          <div className="support-avatar-bg">
            <Headset size={20} color="var(--brand-blue)" />
          </div>
          <div className="support-header-text">
            <h2>Support Chat</h2>
            <p>We usually reply within 24h</p>
          </div>
        </div>
      </div>

      {/* Chat List Area */}
      <div className="chat-list-container">
        <div className="chat-list">
          {messages.map((msg, index) => (
            <ChatBubble key={msg.id || index} msg={msg} />
          ))}
          {/* Invisible element to anchor the auto-scroll */}
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Input Area */}
      <div className="chat-input-area">
        <div className="chat-input-wrapper">
          <textarea
            className="chat-input-field"
            placeholder="Type your message..."
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            rows={1}
          />
          
          <button 
            className="send-button"
            onClick={handleSend}
            disabled={!message.trim() || sending}
          >
            {sending ? (
              <div className="spinner small"></div>
            ) : (
              <Send size={20} className="send-icon" />
            )}
          </button>
        </div>
      </div>

    </div>
  );
};

export default Support;
